import { hideToast, showSuccessToast, showToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import type { IToast } from "app/components/React/Toast/types";
import { i18nFilter } from "app/filters/i18nFilter";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import { getAccountsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";
import { getContactsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/contactsFields";
import multiSelectorPanelTrackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { hubspotDomainService } from "../../../../../modules/crm-export/utils/exportDomainService";
import type { FetchDomainsFn, FetchDomainsParams } from "../../../components/types";
import {
    exportDomainsToAsync,
    fetchHsExportSettingsAsync,
    saveExportSettingsAsync,
    saveHsAllSettingsError,
    addNewSettingsExport,
    deleteSettingExport,
    revertSettingsExport,
    updateHsSettingsExport,
} from "../../../store/actions/export/hubspot";
import { createExportSettingsDto, deleteSetting, enrichExportSettings, groupByRecordType } from "../../../utils/export";
import { convertIntegrationTypeForApiSave, updateSettings } from "../../../utils/utils";
import { managementActions } from "../../action-creators";
import { getPreparedExportSettings } from "../../action-utils";
import { selectCacheToken, selectCurrentExportTab } from "../../selectors/common";
import {
    makeSelectHsColumns,
    selectHsCurrentSettings,
    selectHsPrevSettingsByCurrentType,
    selectHsSettings,
} from "../../selectors/hubspot/exportSelectors";
import type { ExportName, ExportSetting, HubspotExportName, HubspotPreDefinedType, NewExportSetting } from "../../type";
import { exportToCrmToastThunk } from "../common";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

export const fetchHsExportSettingsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(fetchHsExportSettingsAsync.request());

        try {
            const settings = await deps.si.api.hubspotExportSettings.fetchHsExportSettings(signal);
            const { accounts, contacts } = groupByRecordType(settings);

            dispatch(
                fetchHsExportSettingsAsync.success({
                    accounts: enrichExportSettings(accounts, getAccountsFields()),
                    contacts: enrichExportSettings(contacts, getContactsFields("hubspot")),
                }),
            );
        } catch (error) {
            dispatch(fetchHsExportSettingsAsync.failure(error));
        }
    };

export const updateHsExportSettingsThunk =
    (oldSetting: ExportSetting, newSetting: NewExportSetting, exportName: ExportName, isCrmEnrichmentEnabled = false) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings = selectHsCurrentSettings(getState());
        const typeName = selectCurrentExportTab(getState());
        const columns = makeSelectHsColumns(typeName as HubspotExportName)(getState());

        const updatedSettings = updateSettings({
            settings,
            oldSetting,
            exportName,
            newSetting,
            columns,
            isCrmEnrichmentEnabled,
        });

        dispatch(updateHsSettingsExport({ typeName, settings: updatedSettings }));
    };

const saveHsExportSettingsThunk =
    (typeName: ExportName) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(saveExportSettingsAsync.request());

        try {
            const state = getState();
            const settings: readonly ExportSetting[] = selectHsSettings(state)[typeName as HubspotExportName];

            const mappedSettings = createExportSettingsDto(settings);

            const exportName = convertIntegrationTypeForApiSave(typeName) as HubspotPreDefinedType;

            const { value } = await deps.si.api.hubspotExportSettings.saveExportSettings(
                { fields: mappedSettings },
                exportName,
            );

            if (value) {
                dispatch(saveExportSettingsAsync.success({ typeName, settings }));
            } else {
                dispatch(saveExportSettingsAsync.failure("Couldn't export to HS"));
            }
            return value;
        } catch (error) {
            dispatch(saveExportSettingsAsync.failure(error));
            return false;
        }
    };

export const addSettingsExportThunk = (settings: string[], exportName: ExportName, isCrmEnrichmentEnabled = false) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const existingSettings = selectHsCurrentSettings(getState());
        const preparedSettings = getPreparedExportSettings({
            settings,
            exportName,
            integration: "hubspot",
            isCrmEnrichmentEnabled,
            existingSettings,
        });
        dispatch(addNewSettingsExport({ typeName, settings: preparedSettings }));
    };
};

export const deleteHsSettingLocalThunk = (field: string) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const currentSettings: ExportSetting[] = selectHsCurrentSettings(getState());
        const typeName = selectCurrentExportTab(getState());

        const settings = currentSettings.filter((setting) => setting.field !== field);

        dispatch(updateHsSettingsExport({ typeName, settings }));
    };
};

const deleteSettingToastExportThunk = (settings: ExportSetting) => {
    return (dispatch: ThunkDispatchCommon) => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text: i18nFilter()("si.management.sf.settings_export.toast.delete.content", {
                        fieldName: settings.title,
                    }),
                }),
            ),
        );
    };
};

const exportingDomainsToastExportThunk = (toast: IToast) => {
    return (dispatch: ThunkDispatchCommon) => {
        const { getToastComponent } = hubspotDomainService;

        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));
    };
};

export const deleteExportSettingThunk = (settingToDelete: ExportSetting) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings: ExportSetting[] = selectHsCurrentSettings(getState());
        const typeName = selectCurrentExportTab(getState());

        const { updatedSettings, deletedSetting } = deleteSetting(settings, settingToDelete);
        if (deletedSetting) {
            dispatch(deleteSettingExport({ typeName, settings: updatedSettings }));
            dispatch(deleteSettingToastExportThunk(deletedSetting));
        }
    };
};

const exportDomainsToWithNotificationThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId: string | undefined,
    toast: IToast,
) => {
    return async (dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) => {
        const { getErrorToastTitle, getSuccessToastTitle } = hubspotDomainService;

        try {
            const result = await deps.si.api.hubspotExportSettings.exportDomainsTo(
                domains,
                countries,
                leadsType,
                listId,
            );
            if (result) {
                const { processedRecords, exportHistoryUrl } = result;
                dispatch(exportDomainsToAsync.success());
                dispatch(hideToast(toast));
                dispatch(
                    exportToCrmToastThunk(getSuccessToastTitle(processedRecords, domains.length), exportHistoryUrl),
                );
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.hubspot,
                    "success",
                    domains.length,
                    processedRecords,
                );
            } else {
                dispatch(hideToast(toast));
                dispatch(exportToCrmToastThunk(getErrorToastTitle()));
                dispatch(exportDomainsToAsync.failure("Couldn't export to HS"));
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.hubspot,
                    "fail",
                    domains.length,
                    0,
                );
            }
        } catch (error) {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToAsync.failure(error));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
            multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                ExportIntegrations.hubspot,
                "fail",
                domains.length,
                0,
            );
        }
    };
};

export const revertHsSettingsThunk = () => {
    return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings = selectHsPrevSettingsByCurrentType(getState());
        const typeName = selectCurrentExportTab(getState());

        dispatch(revertSettingsExport({ typeName, settings }));
    };
};

export const createExportTopNDomainsToHsThunk =
    <T extends FetchDomainsParams>(fetchDomainsThunk: FetchDomainsFn<T>) =>
    (params: T) =>
    async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToAsync.request());
        const { createToast, getErrorToastTitle, getExportingToastTitle } = hubspotDomainService;

        const toast = createToast(getExportingToastTitle(params.domainsCount));

        dispatch(exportingDomainsToastExportThunk(toast));

        const domains = await fetchDomainsThunk(params);

        if (domains?.length) {
            await dispatch(
                exportDomainsToWithNotificationThunk(
                    domains,
                    params.countries,
                    params.leadsType,
                    params.listParams?.id,
                    toast,
                ),
            );
        } else {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToAsync.failure("Couldn't prepare domains to export to HS"));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
        }
    };

export const exportDomainsToHsThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId?: string,
) => {
    return async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToAsync.request());
        const { createToast, getExportingToastTitle } = hubspotDomainService;

        const toast = createToast(getExportingToastTitle(domains.length));

        dispatch(exportingDomainsToastExportThunk(toast));
        await dispatch(exportDomainsToWithNotificationThunk(domains, countries, leadsType, listId, toast));
    };
};

export const getHsColumnsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(managementActions.hubspot.getHsColumnsAsync.request());
        const cacheToken = selectCacheToken(getState());

        try {
            const [accounts, contacts] = await Promise.all([
                deps.si.api.management.getHsColumns("account", cacheToken, signal),
                deps.si.api.management.getHsColumns("contact", cacheToken, signal),
            ]);

            dispatch(managementActions.hubspot.getHsColumnsAsync.success({ accounts, contacts }));
        } catch (error) {
            dispatch(managementActions.hubspot.getHsColumnsAsync.failure(error));
        }
    };

export const saveHsAllSettingsThunk =
    (changedTabs: ExportName[]) =>
    async (dispatch: ThunkDispatchCommon): Promise<{ successfullySavedSettings: ExportName[] } | undefined> => {
        try {
            const saveSettingPromises = changedTabs.map((tabName) => {
                return dispatch(saveHsExportSettingsThunk(tabName)).then(
                    (isSuccess) => [tabName, isSuccess] as [ExportName, boolean],
                );
            });
            const saveSettingResults = await Promise.all(saveSettingPromises);

            const successfullySavedSettings = saveSettingResults
                .filter(([_, isSuccess]) => isSuccess)
                .map(([exportName]) => exportName);

            return { successfullySavedSettings };
        } catch (error) {
            dispatch(saveHsAllSettingsError(error));
        }
    };
